'use client'

import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { RefreshCcw, Shapes } from 'lucide-react'
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts'

import type { ChartConfig } from '@ui/web/components'
import { Button, ChartContainer, ChartTooltip, ChartTooltipContent, NumberFlow, Skeleton } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import { useSharedTRPC } from '../../../context'

// Mirror the dashboard lesson-trend chart: teacher-assigned XP vs self-practice XP vs badge XP
// (see lesson-trend/charts.tsx). Badge XP has no enrollment behind it, so it is its own series —
// it is also what used to make this chart total less than the "Pengalaman Belajar" card beside it.
const ACTIVITY_CONFIG = {
  assignedXp: { label: 'Tugas (guru)', color: 'var(--chart-1)' },
  selfXp: { label: 'Latihan (mandiri)', color: 'var(--chart-5)' },
  badgeXp: { label: 'Lencana', color: 'var(--chart-3)' },
} satisfies ChartConfig

interface DailyLesson {
  date: Date | string
  assignedXp: number
  selfXp: number
  badgeXp: number
}

interface SkillXp {
  id: string
  name: string
  iconUrl: string | null
  cumulativeEarnedXp: number
}

function formatDayLabel(value: Date | string): string {
  return new Date(value).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })
}

function toUtcDayKey(date: Date): string {
  const y = date.getUTCFullYear()
  const m = String(date.getUTCMonth() + 1).padStart(2, '0')
  const d = String(date.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** One bar per calendar day in [from, to], filling days without lessons with zero XP. */
function fillDailyRange(
  daily: DailyLesson[],
  from: Date,
  to: Date,
): { date: string; assignedXp: number; selfXp: number; badgeXp: number }[] {
  const byDay = new Map<string, { assignedXp: number; selfXp: number; badgeXp: number }>()
  for (const d of daily) {
    byDay.set(toUtcDayKey(new Date(d.date)), { assignedXp: d.assignedXp, selfXp: d.selfXp, badgeXp: d.badgeXp })
  }

  const cursor = new Date(from)
  cursor.setUTCHours(0, 0, 0, 0)
  const end = new Date(to)
  end.setUTCHours(0, 0, 0, 0)

  if (end.getTime() < cursor.getTime()) {
    return [{ date: cursor.toISOString(), assignedXp: 0, selfXp: 0, badgeXp: 0 }]
  }

  const result: { date: string; assignedXp: number; selfXp: number; badgeXp: number }[] = []
  while (cursor.getTime() <= end.getTime()) {
    const day = byDay.get(toUtcDayKey(cursor))
    result.push({
      date: new Date(cursor).toISOString(),
      assignedXp: day?.assignedXp ?? 0,
      selfXp: day?.selfXp ?? 0,
      badgeXp: day?.badgeXp ?? 0,
    })
    cursor.setUTCDate(cursor.getUTCDate() + 1)
  }

  return result
}

// Level curve — mirrors getLevelFromXP in the child-profile skill radar (header/skill-radar.tsx):
// 500 XP/level for the first 5 levels, then 800 XP/level. The progress bar fills toward the next level.
const MAX_LEVEL = 100
const XP_FIRST_PHASE = 500
const XP_SECOND_PHASE = 800
const LEVELS_FIRST_PHASE = 5
const XP_TO_LEVEL_5 = LEVELS_FIRST_PHASE * XP_FIRST_PHASE

function getLevelFromXp(xp: number): number {
  if (xp < XP_FIRST_PHASE) return 0
  if (xp < XP_TO_LEVEL_5) return Math.floor(xp / XP_FIRST_PHASE)
  const additional = Math.floor((xp - XP_TO_LEVEL_5) / XP_SECOND_PHASE)
  return Math.min(LEVELS_FIRST_PHASE + additional, MAX_LEVEL)
}

function xpForLevel(level: number): number {
  if (level <= LEVELS_FIRST_PHASE) return level * XP_FIRST_PHASE
  return XP_TO_LEVEL_5 + (level - LEVELS_FIRST_PHASE) * XP_SECOND_PHASE
}

/** Current level + how far the XP has progressed toward the next level (0-100). */
function getLevelProgress(xp: number): { level: number; fillPct: number } {
  const level = getLevelFromXp(xp)
  if (level >= MAX_LEVEL) return { level, fillPct: 100 }
  const start = xpForLevel(level)
  const next = xpForLevel(level + 1)
  const fillPct = next > start ? ((xp - start) / (next - start)) * 100 : 0
  return { level, fillPct }
}

// ---------------------------------------------------------------------------
// Building blocks — card styling mirrors the reading overview (reading-overview.tsx).
// ---------------------------------------------------------------------------

const chartCardClassName =
  'srd:flex srd:flex-col srd:gap-2 srd:rounded-lg srd:border srd:border-grey-30 srd:bg-neutral-white srd:p-3'

function ActivityPanel({
  data,
  avgXp,
}: {
  data: { date: string; assignedXp: number; selfXp: number; badgeXp: number }[]
  avgXp: number
}) {
  return (
    <section className={cn(chartCardClassName, 'srd:lg:col-span-2')}>
      <p className={cn(typographyVariants({ variant: 'title-md' }))}>Aktivitas belajar</p>

      <div className='srd:mt-2 srd:flex srd:flex-1 srd:flex-col srd:gap-2'>
        <ChartContainer
          config={ACTIVITY_CONFIG}
          className={cn('srd:min-h-[220px] srd:h-auto srd:aspect-auto srd:w-full')}
        >
          <BarChart accessibilityLayer data={data} margin={{ left: 0, right: 8, top: 16, bottom: 8 }}>
            <CartesianGrid vertical={false} strokeDasharray='4 4' />
            <XAxis
              dataKey='date'
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={(v) => formatDayLabel(v as string)}
              minTickGap={24}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              width={40}
              domain={[0, 'auto']}
              allowDecimals={false}
              tickFormatter={(v) => (v === 0 ? '0' : `${v}XP`)}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent className='srd:min-w-48' labelFormatter={(v) => formatDayLabel(String(v))} />
              }
            />
            <Bar dataKey='assignedXp' fill='var(--color-assignedXp)' radius={[4, 4, 0, 0]} />
            <Bar dataKey='selfXp' fill='var(--color-selfXp)' radius={[4, 4, 0, 0]} />
            <Bar dataKey='badgeXp' fill='var(--color-badgeXp)' radius={[4, 4, 0, 0]} />
          </BarChart>
        </ChartContainer>

        <div className='srd:flex srd:shrink-0 srd:flex-wrap srd:items-center srd:justify-center srd:gap-x-3 srd:gap-y-1'>
          {(
            Object.entries(ACTIVITY_CONFIG) as [
              keyof typeof ACTIVITY_CONFIG,
              (typeof ACTIVITY_CONFIG)[keyof typeof ACTIVITY_CONFIG],
            ][]
          ).map(([key, { label, color }]) => (
            <span
              key={key}
              className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:flex srd:items-center srd:gap-1')}
            >
              <span className='srd:size-2 srd:rounded-full' style={{ backgroundColor: color }} />
              {label}
            </span>
          ))}
        </div>

        <div className='srd:mt-auto srd:flex srd:flex-col srd:gap-y-1 srd:border-t srd:border-outline-variant/50 srd:pt-2'>
          <p className={cn(typographyVariants({ variant: 'label-sm' }), 'srd:text-grey-100')}>Rata-rata per hari</p>
          <NumberFlow
            value={avgXp}
            suffix=' XP'
            format={{ minimumFractionDigits: 0, maximumFractionDigits: 1 }}
            className={cn(typographyVariants({ variant: 'body-md' }), 'srd:font-bold')}
          />
        </div>
      </div>
    </section>
  )
}

function ExperienceRow({
  icon,
  title,
  value,
  fillPct,
  emphasized,
}: {
  icon?: ReactNode
  title: string
  value: number
  fillPct?: number
  emphasized?: boolean
}) {
  const titleClassName = cn(
    emphasized
      ? cn(typographyVariants({ variant: 'title-sm' }), 'srd:font-semibold')
      : cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-neutral-black'),
    'srd:min-w-0 srd:truncate',
  )
  const valueNode = (
    <NumberFlow
      value={value}
      suffix='XP'
      format={{ minimumFractionDigits: 0, maximumFractionDigits: 0 }}
      className={cn(
        emphasized
          ? cn(typographyVariants({ variant: 'body-lg' }), 'srd:font-bold')
          : cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-neutral-black'),
        'srd:shrink-0',
      )}
    />
  )

  return (
    <div className='srd:flex srd:items-center srd:gap-3'>
      {icon}
      <div className='srd:flex srd:min-w-0 srd:flex-1 srd:flex-col srd:gap-1'>
        {fillPct !== undefined ? (
          <>
            <p className={titleClassName}>{title}</p>
            {/* Value sits on the bar's row so it lines up with the bar, not the name. */}
            <div className='srd:flex srd:items-center srd:gap-3'>
              <div className='srd:h-2 srd:flex-1 srd:overflow-hidden srd:rounded-full srd:bg-grey-20'>
                <div
                  className='srd:h-full srd:rounded-full srd:bg-purple-100'
                  style={{ width: `${Math.max(0, Math.min(100, fillPct))}%` }}
                />
              </div>
              {valueNode}
            </div>
          </>
        ) : (
          <div className='srd:flex srd:items-center srd:justify-between srd:gap-3'>
            <p className={titleClassName}>{title}</p>
            {valueNode}
          </div>
        )}
      </div>
    </div>
  )
}

function ExperiencePanel({ skills, totalXp }: { skills: SkillXp[]; totalXp: number }) {
  return (
    <section className={chartCardClassName}>
      <p className={cn(typographyVariants({ variant: 'title-md' }))}>Pengalaman Belajar</p>

      <ExperienceRow title='Total' value={totalXp} emphasized />

      {skills.length > 0 && (
        <div className='srd:flex srd:flex-col srd:gap-3 srd:border-t srd:border-outline-variant/50 srd:pt-3'>
          {skills.map((skill) => {
            const { fillPct } = getLevelProgress(skill.cumulativeEarnedXp)
            return (
              <ExperienceRow
                key={skill.id}
                icon={
                  <span className='srd:flex srd:size-8 srd:shrink-0 srd:items-center srd:justify-center srd:overflow-hidden srd:rounded-lg srd:bg-grey-10 srd:text-grey-100'>
                    {skill.iconUrl ? (
                      <img src={skill.iconUrl} alt='' className='srd:size-full srd:object-cover' loading='lazy' />
                    ) : (
                      <Shapes className='srd:size-5' />
                    )}
                  </span>
                }
                title={skill.name}
                value={skill.cumulativeEarnedXp}
                fillPct={fillPct}
              />
            )
          })}
        </div>
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Public component — real data from metrics.getLessonMetrics + metrics.getCumulativeEarnedXp, driven by [from, to].
// ---------------------------------------------------------------------------

interface LessonOverviewProps {
  childId: string
  from: Date
  to: Date
  className?: string
}

export function LessonOverview({ childId, from, to, className }: LessonOverviewProps) {
  const trpc = useSharedTRPC()

  const lessonQuery = useQuery(trpc.metrics.getLessonMetrics.queryOptions({ userId: [childId], from, to }))
  const xpQuery = useQuery(trpc.metrics.getCumulativeEarnedXp.queryOptions({ userId: childId, from, to }))

  const isError = lessonQuery.isError || xpQuery.isError
  const isPending = lessonQuery.isPending || xpQuery.isPending

  if (isError) {
    return (
      <section
        className={cn(
          'srd:flex srd:w-full srd:flex-col srd:items-center srd:justify-center srd:gap-2 srd:rounded-lg srd:border srd:border-grey-30 srd:bg-neutral-white srd:py-10',
          className,
        )}
      >
        <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-main-red-100')}>
          Gagal memuat metrik belajar
        </p>
        <Button
          variant='outline'
          size='sm'
          onClick={() => {
            if (lessonQuery.isError) void lessonQuery.refetch()
            if (xpQuery.isError) void xpQuery.refetch()
          }}
        >
          <RefreshCcw className='srd:size-4 srd:mr-2 srd:text-main-blue-100' />
          Coba lagi
        </Button>
      </section>
    )
  }

  if (isPending) {
    return (
      <div className={cn('srd:flex srd:w-full srd:flex-col srd:gap-4', className)}>
        <div className='srd:grid srd:grid-cols-1 srd:items-stretch srd:gap-3 srd:lg:grid-cols-3'>
          <Skeleton className='srd:h-[320px] srd:w-full srd:rounded-lg srd:lg:col-span-2' />
          <Skeleton className='srd:h-[320px] srd:w-full srd:rounded-lg' />
        </div>
      </div>
    )
  }

  const row = lessonQuery.data.metrics.find((m) => m.id === childId)
  const daily = row?.dailyMetrics ?? []

  const chartData = fillDailyRange(daily, from, to)
  const periodDays = chartData.length
  const totalDailyXp = chartData.reduce((s, d) => s + d.assignedXp + d.selfXp + d.badgeXp, 0)
  const avgXpPerDay = periodDays > 0 ? totalDailyXp / periodDays : 0

  const skills = xpQuery.data
  const totalXp = skills.reduce((s, sk) => s + sk.cumulativeEarnedXp, 0)

  return (
    <div className={cn('srd:flex srd:w-full srd:flex-col srd:gap-4', className)}>
      <div className='srd:grid srd:grid-cols-1 srd:items-stretch srd:gap-3 srd:lg:grid-cols-3'>
        <ActivityPanel data={chartData} avgXp={avgXpPerDay} />
        <ExperiencePanel skills={skills} totalXp={totalXp} />
      </div>
    </div>
  )
}
